
type LogLevel = 'error' | 'warn' | 'info' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
}

class Logger {
  private isDevelopment = import.meta.env.MODE === 'development';
  private isProduction = import.meta.env.MODE === 'production';

  private formatMessage(level: LogLevel, message: string, data?: any): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) return true;
    if (this.isProduction) {
      // Only log errors and warnings in production
      return level === 'error' || level === 'warn';
    }
    return true;
  }

  error(message: string, data?: any): void {
    if (this.shouldLog('error')) {
      const entry = this.formatMessage('error', message, data);
      console.error(`[${entry.timestamp}] ERROR: ${message}`, data || '');
      
      // In production, also send to monitoring service
      if (this.isProduction && typeof window !== 'undefined') {
        // Send to Sentry or other monitoring service
        import('@/utils/monitoring').then(({ reportError }) => {
          reportError(new Error(message), data);
        });
      }
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog('warn')) {
      const entry = this.formatMessage('warn', message, data);
      console.warn(`[${entry.timestamp}] WARN: ${message}`, data || '');
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog('info')) {
      const entry = this.formatMessage('info', message, data);
      console.info(`[${entry.timestamp}] INFO: ${message}`, data || '');
    }
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog('debug')) {
      const entry = this.formatMessage('debug', message, data);
      console.debug(`[${entry.timestamp}] DEBUG: ${message}`, data || '');
    }
  }
}

export const logger = new Logger();
